/**
 * Component   : UserManagement
 * Description : Adding a new User
 */
import { Grid, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { callAPI, clearRequest, EPhone, formatPhoneNumber, handleApiError, Method, updateMainHeader } from "../../commonFunctions/CommonFunctions";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";
import WSPhone from "../../Components/Phone/WSPhone";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSTextField, { CurrencyType, TextType } from "../../Components/TextBox/WSTextBox";

import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import WSDateField from "../../Components/DateField/WSDateField";

export const Employee: React.FC<any> = React.memo((props) => {
    const { showMessage } = useSnackbar();
    const { register, handleSubmit, control, setValue, reset } = useForm();
    const navigate = useNavigate();
    const params = useParams();

    const [userPermissions, setUserPermissions] = useState<any>([]);
    const [permissions, setPermissions] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const id = params?.id;

    useEffect(() => {
        updateMainHeader("Add User");
        breadCrumbsStore.updateBreadCrumbs([
            { "Employee": "/employees/list" },
            { [id ? 'Edit Employee' : "Create Employee"]: `` },
        ]);
    });

    useEffect(() => () => {
        reset()
    }, [])

    useEffect(() => {
        async function getEmployeeById(id: any): Promise<void> {
            if (!id) return;

            try {
                const response: any = await callAPI('Employee', Method.GET, {}, id);
                console.log('response employee data', response);

                const data = response?.data;
                if (!data) return;

                // permissions (if needed)
                if (data.permissions) {
                    setUserPermissions(data.permissions);
                }

                // Explicit field mapping
                setValue('name', data.name || '');
                setValue(
                    'phoneNumber',
                    data.phoneNumber
                        ? formatPhoneNumber(data.phoneNumber, EPhone.AddHyphen)
                        : ''
                );
                setValue('expectedSalary', data.expectedSalary ?? '');
                setValue('skill', data.skill || '');
                setValue('homeDate', data.homeDate || '');
                setValue('previousWorkPlace', data.previousWorkPlace || '');
                setValue('location', data.location || '');

            } catch (error) {
                showMessage('Error fetching employee details', 'error');
            }
        }

        getEmployeeById(id);
    }, [id, setValue]);

    const handleCancelClick = () => {
        navigate("/employees/list");
    };

    /**
     * Method for Add/Update the data
     * @param data 
     */
    const onSubmit = async (data: any) => {
        console.log('try to save data pls wait', data)
        console.log('data', data)
        try {
            setLoading(true)
            const request: any = await setRequest(data);
            console.log('request beofre save', request);

            if (id) {
                const response: any = await callAPI(`Employee`, Method.PUT, request, id);
                console.log('rsponse save', response)
                if (response?.errors) {
                    const mess = handleApiError(response)
                    showMessage(mess, 'error');
                }
                else if (response) {
                    showMessage(response?.message, response.success === true ? "success" : "error");
                    response.success === true && navigate("/employees/list");
                }
            } else {
                const response: any = await callAPI('/Employee', Method.POST, request);
                console.log('rsponse save', response)
                if (response?.errors) {
                    const mess = handleApiError(response)
                    showMessage(mess, 'error');
                }
                else if (response) {
                    showMessage(response?.message, response.success === true ? "success" : "error");
                    response.success === true && navigate("/employees/list");
                }
            }
        } catch (e: any) {
            showMessage(e.error);
        }
        finally {
            setLoading(false)
        }
    };

    /**
     * Method to set the request for the user
     * @param data 
     * @returns 
     */
    async function setRequest(data: any) {
        let request: any = {
            name: data.name,
            phoneNumber: formatPhoneNumber(data?.phoneNumber, EPhone.RemoveHyphen) || "",
            expectedSalary: Number(data.expectedSalary),
            skill: data.skill,
            homeDate: data.homeDate,
            previousWorkPlace: data.previousWorkPlace,
            location: data.location
        };
        return clearRequest(request);
    }
    const actions: IHeaderListActions[] =
        [
            { type: 'button', text: 'Cancel', size: 'small', onClick: handleCancelClick, variant: 'outlined', color: 'primary' },
            { type: 'button', text: 'Save', size: 'small', loading: loading, buttonType: 'submit', variant: 'outlined', color: 'primary' }
        ]

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <HeaderList
                    title={!id ? "Add Employee Entry" : "Employee Details"}
                    isBackButton={true}
                    url="/employee/list"
                    type="card"
                    actions={actions}
                    isDivider
                    headerSx={{ height: 50 }}
                />

                <Paper
                    elevation={3}
                    style={{
                        paddingLeft: 20,
                        paddingRight: 20,
                        marginTop: 0,
                        paddingBottom: 25,
                        border: '1px solid #ccc',
                        borderRadius: '8px'
                    }}
                >
                    <Grid xs={12} container spacing={2} style={{ marginTop: 10 }}>

                        <Grid item xs={12}>
                            <Typography sx={{ fontSize: '.9rem' }} color="gray">
                                Employee Information
                            </Typography>
                        </Grid>

                        {/* Name */}
                        <Grid item sm={6} xs={12}>
                            <WSTextField
                                control={control}
                                id="name"
                                {...register('name')}
                                name="name"
                                rules={{ required: true }}
                                label="Full Name *"
                            />
                        </Grid>

                        {/* Phone */}
                        <Grid item sm={6} xs={12}>
                            <WSPhone
                                label="Phone Number *"
                                rules={{ required: true }}
                                control={control}
                                id="phoneNumber"
                                {...register('phoneNumber')}
                                name="phoneNumber"
                            />
                        </Grid>

                        {/* Salary */}
                        <Grid item sm={6} xs={12}>
                            <WSTextField
                                control={control}
                                id="expectedSalary"
                                {...register('expectedSalary')}
                                name="expectedSalary"
                                rules={{ required: false }}
                                label="Expected Salary "
                                type={TextType.Currency}
                                currency={CurrencyType.Rupee}
                            />
                        </Grid>

                        {/* Skill */}
                        <Grid item sm={6} xs={12}>
                            <WSTextField
                                control={control}
                                id="skill"
                                {...register('skill')}
                                name="skill"
                                rules={{ required: false }}
                                label="Skill "
                            />
                        </Grid>

                        {/* Home Return Date */}
                        {/* <Grid item sm={6} xs={12}>
                            <WSDateField
                                id="homeDate"
                                label="Home Return Date *"
                                name="homeDate"
                                control={control}
                                rules={{ required: true }}
                                dateFormat="YYYY-MM-DD"
                            />
                        </Grid> */}

                        {/* Location */}
                        <Grid item sm={6} xs={12}>
                            <WSTextField
                                control={control}
                                id="location"
                                {...register('location')}
                                name="location"
                                rules={{ required: false }}
                                label="Location "
                            />
                        </Grid>

                        {/* Previous Work */}
                        <Grid item sm={12} xs={12}>
                            <WSTextField
                                control={control}
                                id="previousWorkPlace"
                                {...register('previousWorkPlace')}
                                name="previousWorkPlace"
                                rules={{ required: false }}
                                label="Previous Work Place / Company "
                            />
                        </Grid>

                    </Grid>
                </Paper>
            </form>
        </>
    );
});
